import { Box, Container, Flex, Text } from '@chakra-ui/react'
import NavbarOpen from '../NavLinks/NavbarOpen'
import Appbar from './Appbar'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { DateTime } from 'luxon'
import { changeAllowBotStatus, fetchMeetings, fetchMeetings2 } from '../../Api/meetings'
import { useNavigate } from 'react-router-dom'
import { useContext } from 'react'
import { NavbarContext } from '../../Context/NavbarContext'
import { useSelector } from 'react-redux'
import UnvisitedMeeting from './Cards/MeetingCards/UnvisitedMeeting'
import ExpandedMeeting from './Cards/MeetingCards/ExpandedMeeting'
import CollapsedMeeting from './Cards/MeetingCards/CollapsedMeeting'
import DateCard from './Cards/DateCard'

export const compareDate = (a, b) => {
  if (!a || !b) return false
  return a.date === b.date && a.month === b.month && a.year === b.year
}

const getDayFromDateTime = (dateTime) => {
  const firstDayOfMonth = DateTime.fromObject({
    year: parseInt(dateTime.toFormat('yyyy')),
    month: dateTime.toFormat('L'),
  })
  const day = {
    date: parseInt(dateTime.toFormat('dd')),
    day: dateTime.toFormat('ccc'),
    month: dateTime.toFormat('LLLL'),
    year: parseInt(dateTime.toFormat('yyyy')),
    week: `${
      parseInt(dateTime.toFormat('W')) -
      parseInt(firstDayOfMonth.toFormat('W')) +
      1
    }`,
  }
  return day
}

export const formatMeeting = (meeting, tags) => {
  // console.log(tags)
  const timestamp = meeting.start_time
  let dateTime = DateTime.fromMillis(parseInt(timestamp))
  if (meeting.is_recurring) {
    const tz = meeting.event_id.split('_').at(-1)
    dateTime = DateTime.fromObject({
      year: parseInt(tz[0] + tz[1] + tz[2] + tz[3]),
      month: parseInt(tz[4] + tz[5]),
      day: parseInt(tz[6] + tz[7]),
      hour: parseInt(dateTime.toFormat('H')),
      minute: parseInt(dateTime.toFormat('m')),
    }) 
  }
  const day = getDayFromDateTime(dateTime)
  const time = dateTime.toFormat('hh:mm a')
  const diff = DateTime.fromMillis(parseInt(meeting.end_time)).diff(dateTime)


  let previousMeetingDuration
  let previousMeetingTimeSince
  if (meeting.previous_event_event_id) {
    let prevDateTime = DateTime.fromMillis(parseInt(meeting.previous_event_start_time))
    if (meeting.previous_event_event_id.endsWith('00Z')) {
      const tz = meeting.previous_event_event_id.split('_').at(-1)
      prevDateTime = DateTime.fromObject({
        year: parseInt(tz[0] + tz[1] + tz[2] + tz[3]),
        month: parseInt(tz[4] + tz[5]),
        day: parseInt(tz[6] + tz[7]),
        hour: parseInt(prevDateTime.toFormat('H')),
        minute: parseInt(prevDateTime.toFormat('m')),
      })
    }

    const prevDiff = DateTime.fromMillis(parseInt(meeting.previous_event_end_time)).diff(prevDateTime)
    previousMeetingDuration = prevDiff.toFormat('m') + ' minutes'

    const t = DateTime.fromMillis(dateTime.diff( prevDateTime , 'days').toMillis() - 86400000).toFormat('d')
    previousMeetingTimeSince = `${t} day${t === '1' ? '' : 's'} ago`
  }

  let recurringPreviousMeetingDuration
  let recurringPreviousMeetingTimeSince
  if (meeting.reccuring_previous_event_event_id) {
    let prevDateTime = DateTime.fromMillis(parseInt(meeting.reccuring_previous_event_start_time))
    if (meeting.reccuring_previous_event_event_id.endsWith('00Z')) {
      const tz = meeting.reccuring_previous_event_event_id.split('_').at(-1)
      prevDateTime = DateTime.fromObject({
        year: parseInt(tz[0] + tz[1] + tz[2] + tz[3]),
        month: parseInt(tz[4] + tz[5]),
        day: parseInt(tz[6] + tz[7]),
        hour: parseInt(prevDateTime.toFormat('H')),
        minute: parseInt(prevDateTime.toFormat('m')),
      })
    }
    // console.log('96', prevDateTime, meeting.reccuring_previous_event_event_id, prevDateTime.diffNow())
    const prevDiff = DateTime.fromMillis(parseInt(meeting.reccuring_previous_event_end_time)).diff(prevDateTime)
    recurringPreviousMeetingDuration = prevDiff.toFormat('m') + ' minutes'

    const t = DateTime.fromMillis(dateTime.diff( prevDateTime , 'days').toMillis() - 86400000).toFormat('d')
    recurringPreviousMeetingTimeSince = `${t} day${t === '1' ? '' : 's'} ago`
  }

  return {
    ...meeting,
    day,
    eventId: meeting.event_id,
    title: meeting.title,
    isRecurring: meeting.is_recurring,
    time,
    meetingDay: dateTime.toFormat('ccc'),
    isUpcoming: dateTime.diffNow().toMillis() > 0,
    summary: meeting.summary,
    duration: diff.toFormat('m') + 'm',
    tags: (meeting.tags ?? [])
      .map((tagName) => {
        if (!tags) return undefined
        const tag = tags.find((t) => t.name === tagName)
        return tag
      })
      .filter((tag) => Boolean(tag)),
    functions: (meeting.user_functions ?? [])
      .map((fName) => ({ function: fName.trim() }))
      .filter((f) => Boolean(f)),
    attendees: meeting.attendees,
    actualTime: dateTime.toMillis(),
    allowBot: meeting.bot_join,

    previousMeetingDuration,
    previousMeetingTimeSince,

    recurringPreviousMeetingDuration,
    recurringPreviousMeetingTimeSince,
  }
}

const Meetings = () => {
  const containerRef = useRef(null)

  const navigate = useNavigate()
  const { dashboardTags } = useContext(NavbarContext)
  const user = useSelector((state) => state?.user.data)

  const [currentDate, setCurrentDate] = useState()
  const [meetings, setMeetings] = useState()

  const [today, setToday] = useState()
  const [highlightedEventId, setHighlightedEventId] = useState('')
  const [highlightedDate, setHighlightedDate] = useState()
  const [todayMeetingId, setTodayMeetingId] = useState('')
  
  useEffect(() => {
    const now = DateTime.local()
    setCurrentDate(now)
    const today = getDayFromDateTime(now)
    setToday(today)
    setHighlightedDate(today)
  }, [])

  const fetchMeetings = useCallback(async () => {
    const apiResponse = await fetchMeetings2(currentDate.toMillis())
    setHighlightedEventId('')
    setMeetings(apiResponse.meetings.map((m) => formatMeeting(m, dashboardTags)))
    
  }, [currentDate])

  useEffect(() => {
    if (!currentDate) return
    fetchMeetings()
  }, [currentDate])

  const scrollOnInit = useCallback((behavior='instant') => {
    if (!meetings || meetings.length === 0) return
    const element = containerRef.current
    if (!element) return
    let scrollToIndex
    
    // show first today upcoming
    const todayUpcomingIndex = [...meetings].reverse().findIndex((meeting) =>
      compareDate(meeting.day, today) && meeting.isUpcoming
    )

    // else show last of today
    const todayIndex =  [...meetings].findIndex((meeting) =>
      compareDate(meeting.day, today)
    )

    // else show first upcoming
    const upcomingIndex = [...meetings].reverse().findIndex((meeting) =>
      meeting.isUpcoming
    )

    // else topmost meeting
    const defaultIndex = 0

    if (todayUpcomingIndex > -1) {
      scrollToIndex = meetings.length - 1 - todayUpcomingIndex
      setTodayMeetingId(meetings[scrollToIndex].id)
    } else if (todayIndex > -1) {
      scrollToIndex = todayIndex
      setTodayMeetingId(meetings[scrollToIndex].id)
    } else if (upcomingIndex > -1) {
      scrollToIndex = meetings.length - 1 - upcomingIndex
    } else {
      scrollToIndex = defaultIndex
    }

    // const highlightedIndex = meetings.findIndex(
    //   (meeting) => meeting.id === highlightedEventId
    // )


    // console.log(scrollToIndex)
    // console.log(meetings, nextLoading.current, todayIndex, meetings.length, highlightedIndex)
    // 86, 280 is height of collapsed, expanded
    let delta = 0 

    // console.log(scrollToIndex, 86 * scrollToIndex + delta, element.scrollHeight)
    element.scrollTo({ top: 86 * scrollToIndex + delta, behavior: behavior })
    
    setHighlightedEventId(meetings[scrollToIndex].id)
    setHighlightedDate(meetings[scrollToIndex].day)

    setTimeout(() => {
      
    }, 600)
  }, [meetings, highlightedEventId])

  useEffect(() => {
    scrollOnInit()
    setTimeout(() => scrollOnInit('smooth'), 300)
  }, [meetings, containerRef])

  const renderMeeting = (meeting, index) => {
    if (!meeting.id) {
      return (
        <Flex h='141px' flexDirection='column'>
          <Text display='flex' flex={1} alignItems='center' justifyContent='center' fontWeight='500' color='#000000' textAlign='center'>
            No meetings to show
          </Text>
          <Box bg='#CFCFCF' w='full' h='2px' mt={4} />
        </Flex>
      )
    }

    if (meeting.id === highlightedEventId) {
      if (!meeting.summary && !meeting.isUpcoming) {
        return <UnvisitedMeeting meeting={meeting} index={index} />
      }
      return (
        <ExpandedMeeting
          meeting={meeting}
          index={meeting.id}
          user={user}
          changeAllowBotState={(status) => {
            changeAllowBotStatus(meeting.eventId, status)
            const meetingsCopy = [...meetings]
            meetingsCopy[index].allowBot = status
            setMeetings(meetingsCopy)
          }}
          handleDetailsClick={() => {
            if (meeting.isUpcoming) {
              window.open(meeting.link, '_blank')
              return
            }
            navigate(
              `/transcripts-summaries/summary?eventId=${meeting.eventId}&tab=HIGH&eventName=${meeting.title}`
            )
          }}
        />
      )
    }

    return (
      <CollapsedMeeting
        meeting={meeting}
        index={meeting.id}
        setActive={() => {
          setHighlightedEventId(meeting.id)
          // handled while scrolling
          // setHighlightedDate(meeting.day)
        }}
      />
    )
  }

  return (
    <Container bg='brandBackground' maxW='full' px='131px' pt='48px' h='100vh'>
      <Box left='0' top='0' position='absolute'>
        <NavbarOpen />
      </Box>

      <Container
        maxW='991px'
        mr={0}
        display='flex'
        flexDirection='column'
        h='full'
      >
        <Appbar currentDate={currentDate} setCurrentDate={setCurrentDate} scrollToToday={() => {
          const now = DateTime.local()

          if (currentDate.toFormat('W') === now.toFormat('W')) {
            scrollOnInit('smooth')
          } else {
            setCurrentDate(now)
          }
        }} />
      
        <Box mt={6} />

        <Box
          w='full'
          minH={20} 
          bg='linear-gradient(180deg, #FBFBFB 0%, rgba(251, 251, 251, 0) 100%)'
          mb='-80px'
          zIndex={1}
          pointerEvents='none'
        />

        <Flex
          ref={containerRef}
          flexDirection='column'
          flex={1}
          className='noScrollbar'
          overflow='scroll'
          scrollSnapStop='always'
          // scrollSnapType='y mandatory' TODO snap scrolling (add scrollSnapAlign='start'  to child)
          py='86px'
        >
          {meetings?.map((meeting, meetingIndex) => {
            const currentMeeting = meeting
            return (
              <Flex key={`${currentMeeting.id}`}>
                <Flex
                  flexDirection='column'
                  alignItems='center'
                  w='55px'
                  mr='26px'
                >
                  <DateCard day={currentMeeting.day} id={currentMeeting.id} todayMeetingId={todayMeetingId} />
                </Flex>
                <Flex flex={1} flexDirection='column'>
                  {renderMeeting(currentMeeting, meetingIndex)}
                </Flex>
              </Flex>
            )
          })}
        </Flex>

      </Container>
    </Container>
  )
}

export default Meetings